import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import apiService, { type AdminBrand } from "../services/api";
import {
  BRAND_CATEGORIES,
  BRAND_CATEGORY_LABELS,
} from "../constants/brandCategories";
import BrandAnalysisDrawer, { adminBrandLogoUrl } from "./BrandAnalysisDrawer";

type RowSaveState = "idle" | "saving" | "saved" | "error";

const parseMentionInput = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
};

const BrandAnalysisAdmin: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [sponsorFilter, setSponsorFilter] = useState("all");
  const [imageFilter, setImageFilter] = useState("all");
  const [sort, setSort] = useState("activity");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [rowStates, setRowStates] = useState<Record<string, RowSaveState>>({});
  const [minMentionsInput, setMinMentionsInput] = useState("");
  const [maxMentionsInput, setMaxMentionsInput] = useState("");
  const [minMentions, setMinMentions] = useState<number | undefined>();
  const [maxMentions, setMaxMentions] = useState<number | undefined>();
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkCategory, setBulkCategory] = useState("");
  const [toast, setToast] = useState<{
    severity: "success" | "error" | "warning";
    message: string;
  } | null>(null);

  const apiSort =
    sort === "activity" && sortDir === "asc" ? "activity-asc" : sort;

  const selectedBrandId = searchParams.get("brandId");

  const openBrandDrawer = useCallback(
    (row: AdminBrand) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("brandId", row._id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeBrandDrawer = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("brandId");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const stopRowClick: React.MouseEventHandler = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      setMinMentions(parseMentionInput(minMentionsInput));
      setMaxMentions(parseMentionInput(maxMentionsInput));
    }, 300);
    return () => clearTimeout(t);
  }, [minMentionsInput, maxMentionsInput]);

  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearch,
    categoryFilter,
    activeFilter,
    sponsorFilter,
    imageFilter,
    minMentions,
    maxMentions,
    sort,
    sortDir,
  ]);

  // Selections hidden by a new filter would otherwise still be acted on.
  useEffect(() => {
    setSelected(new Map());
  }, [
    debouncedSearch,
    categoryFilter,
    activeFilter,
    sponsorFilter,
    imageFilter,
    minMentions,
    maxMentions,
  ]);

  const queryKey = useMemo(
    () =>
      [
        "admin-brands",
        page,
        rowsPerPage,
        debouncedSearch,
        categoryFilter,
        activeFilter,
        sponsorFilter,
        imageFilter,
        minMentions,
        maxMentions,
        apiSort,
      ] as const,
    [
      page,
      rowsPerPage,
      debouncedSearch,
      categoryFilter,
      activeFilter,
      sponsorFilter,
      imageFilter,
      minMentions,
      maxMentions,
      apiSort,
    ],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      apiService.getAdminBrands({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        category: categoryFilter,
        isActive: activeFilter,
        canSponsor: sponsorFilter,
        hasImage: imageFilter,
        minMentions,
        maxMentions,
        sort: apiSort,
      }),
    staleTime: 30_000,
  });

  const patchMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { category?: string | null };
    }) => apiService.patchAdminBrand(id, payload),
    onMutate: ({ id }) => {
      setRowStates((prev) => ({ ...prev, [id]: "saving" }));
    },
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(queryKey, (old: typeof data | undefined) => {
        if (!old) return old;
        return {
          ...old,
          brands: old.brands.map((brand) =>
            brand._id === id
              ? {
                  ...result.brand,
                  paidMentions: brand.paidMentions,
                  lastPaidMentionAt: brand.lastPaidMentionAt,
                }
              : brand,
          ),
        };
      });
      setRowStates((prev) => ({ ...prev, [id]: "saved" }));
      setTimeout(() => {
        setRowStates((prev) =>
          prev[id] === "saved" ? { ...prev, [id]: "idle" } : prev,
        );
      }, 2000);
    },
    onError: (_err, { id }) => {
      setRowStates((prev) => ({ ...prev, [id]: "error" }));
    },
  });

  const handleCategoryChange = (row: AdminBrand, category: string) => {
    const next = category || null;
    if ((row.category ?? null) === next) return;
    patchMutation.mutate({ id: row._id, payload: { category: next } });
  };

  const brands = data?.brands ?? [];
  const total = data?.pagination.total ?? 0;

  const selectedOnPage = brands.filter((b) => selected.has(b._id)).length;
  const allOnPageSelected = brands.length > 0 && selectedOnPage === brands.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;

  const toggleRow = (row: AdminBrand) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row._id)) next.delete(row._id);
      else next.set(row._id, row.name);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) {
        brands.forEach((b) => next.delete(b._id));
      } else {
        brands.forEach((b) => next.set(b._id, b.name));
      }
      return next;
    });
  };

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiService.bulkDeleteAdminBrands(ids),
    onSuccess: (result) => {
      setConfirmBulkDelete(false);
      setSelected((prev) => {
        const next = new Map(prev);
        result.deletedBrandIds.forEach((id) => next.delete(id));
        result.notFoundIds.forEach((id) => next.delete(id));
        return next;
      });
      if (selectedBrandId && result.deletedBrandIds.includes(selectedBrandId)) {
        closeBrandDrawer();
      }
      void queryClient.invalidateQueries({ queryKey: ["admin-brands"] });
      const deleted = result.deletedBrandIds.length;
      const failed = result.failedIds.length;
      setToast({
        severity: failed > 0 ? "warning" : "success",
        message:
          `${deleted} marca${deleted === 1 ? "" : "s"} eliminada${deleted === 1 ? "" : "s"}` +
          ` (${result.mentionsDeleted} menciones).` +
          (failed > 0 ? ` ${failed} fallaron.` : ""),
      });
    },
    onError: () => {
      setToast({ severity: "error", message: "No se pudieron eliminar las marcas." });
    },
  });

  const bulkCategoryMutation = useMutation({
    mutationFn: async ({ ids, category }: { ids: string[]; category: string | null }) => {
      const results = await Promise.allSettled(
        ids.map((id) => apiService.patchAdminBrand(id, { category })),
      );
      return {
        ok: results.filter((r) => r.status === "fulfilled").length,
        failed: results.filter((r) => r.status === "rejected").length,
      };
    },
    onSuccess: ({ ok, failed }) => {
      setBulkCategory("");
      void queryClient.invalidateQueries({ queryKey: ["admin-brands"] });
      setToast({
        severity: failed > 0 ? "warning" : "success",
        message:
          `Categoría actualizada en ${ok} marca${ok === 1 ? "" : "s"}.` +
          (failed > 0 ? ` ${failed} fallaron.` : ""),
      });
    },
  });

  const bulkBusy = bulkDeleteMutation.isPending || bulkCategoryMutation.isPending;
  const selectedNames = Array.from(selected.values());

  const selectedBrandSummary = useMemo(() => {
    if (!selectedBrandId) return null;
    return (data?.brands ?? []).find((brand) => brand._id === selectedBrandId) ?? null;
  }, [selectedBrandId, data?.brands]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={600}>
          Marcas
        </Typography>
        <Tooltip title="Actualizar">
          <span>
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap>
          <TextField
            size="small"
            placeholder="Buscar por nombre, alias o slug…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Categoría</InputLabel>
            <Select
              value={categoryFilter}
              label="Categoría"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="uncategorized">Sin categoría</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
              {BRAND_CATEGORIES.map((key) => (
                <MenuItem key={key} value={key}>
                  {BRAND_CATEGORY_LABELS[key] ?? key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Imagen</InputLabel>
            <Select
              value={imageFilter}
              label="Imagen"
              onChange={(e) => setImageFilter(e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="true">Con imagen</MenuItem>
              <MenuItem value="false">Sin imagen</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={activeFilter}
              label="Estado"
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="true">Activas</MenuItem>
              <MenuItem value="false">Inactivas</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Auspicio</InputLabel>
            <Select
              value={sponsorFilter}
              label="Auspicio"
              onChange={(e) => setSponsorFilter(e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="true">Puede auspiciar</MenuItem>
              <MenuItem value="false">No auspicia</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            label="Menciones mín."
            value={minMentionsInput}
            onChange={(e) => setMinMentionsInput(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{ width: 130 }}
          />
          <TextField
            size="small"
            type="number"
            label="Menciones máx."
            value={maxMentionsInput}
            onChange={(e) => setMaxMentionsInput(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{ width: 130 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Orden</InputLabel>
            <Select
              value={sort}
              label="Orden"
              onChange={(e) => {
                setSort(e.target.value);
                setSortDir("desc");
              }}
            >
              <MenuItem value="activity">Menciones</MenuItem>
              <MenuItem value="name">Nombre</MenuItem>
              <MenuItem value="updatedAt">Actualizadas</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          No se pudieron cargar las marcas.
        </Alert>
      )}

      <Paper>
        {selected.size > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            useFlexGap
            flexWrap="wrap"
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: "action.selected",
              borderTopLeftRadius: "inherit",
              borderTopRightRadius: "inherit",
            }}
          >
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {selected.size} seleccionada{selected.size === 1 ? "" : "s"}
              {selected.size > selectedOnPage &&
                ` (${selected.size - selectedOnPage} en otras páginas)`}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Asignar categoría</InputLabel>
              <Select
                value={bulkCategory}
                label="Asignar categoría"
                disabled={bulkBusy}
                onChange={(e) => {
                  const value = e.target.value;
                  setBulkCategory(value);
                  bulkCategoryMutation.mutate({
                    ids: Array.from(selected.keys()),
                    category: value === "__none__" ? null : value,
                  });
                }}
              >
                <MenuItem value="__none__">Sin categoría</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
                {BRAND_CATEGORIES.map((key) => (
                  <MenuItem key={key} value={key}>
                    {BRAND_CATEGORY_LABELS[key] ?? key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={bulkBusy}
              onClick={() => setConfirmBulkDelete(true)}
            >
              Eliminar
            </Button>
            <Button
              disabled={bulkBusy}
              onClick={() => setSelected(new Map())}
            >
              Limpiar selección
            </Button>
            {bulkBusy && <CircularProgress size={20} />}
          </Stack>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected}
                    disabled={brands.length === 0}
                    onChange={togglePage}
                    inputProps={{ "aria-label": "Seleccionar página" }}
                  />
                </TableCell>
                <TableCell>Marca</TableCell>
                <TableCell width={200}>Categoría</TableCell>
                <TableCell width={160} align="right" sortDirection={sort === "activity" ? sortDir : false}>
                  <TableSortLabel
                    active={sort === "activity"}
                    direction={sort === "activity" ? sortDir : "desc"}
                    onClick={() => {
                      if (sort === "activity") {
                        setSortDir((dir) => (dir === "desc" ? "asc" : "desc"));
                        return;
                      }
                      setSort("activity");
                      setSortDir("desc");
                    }}
                  >
                    Menciones
                  </TableSortLabel>
                </TableCell>
                <TableCell>Aliases</TableCell>
                <TableCell width={120}>Estado</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : brands.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No hay marcas que coincidan con los filtros.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                brands.map((row) => {
                  const saveState = rowStates[row._id] ?? "idle";
                  const isRowBusy = saveState === "saving";
                  const imageUrl = adminBrandLogoUrl(row);
                  const categoryValue = row.category ?? "";

                  return (
                    <TableRow
                      key={row._id}
                      hover
                      selected={selectedBrandId === row._id || selected.has(row._id)}
                      onClick={() => openBrandDrawer(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox" onClick={stopRowClick}>
                        <Checkbox
                          size="small"
                          checked={selected.has(row._id)}
                          onChange={() => toggleRow(row)}
                          inputProps={{ "aria-label": `Seleccionar ${row.name}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={imageUrl}
                            alt={row.name}
                            sx={{ width: 36, height: 36 }}
                          >
                            {row.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {row.name}
                            </Typography>
                            {row.slug && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {row.slug}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell onClick={stopRowClick}>
                        <Select
                          size="small"
                          fullWidth
                          value={categoryValue}
                          disabled={isRowBusy}
                          displayEmpty
                          onChange={(e) =>
                            handleCategoryChange(row, e.target.value)
                          }
                        >
                          <MenuItem value="">Sin categoría</MenuItem>
                          <MenuItem value="unknown">Unknown</MenuItem>
                          {BRAND_CATEGORIES.map((key) => (
                            <MenuItem key={key} value={key}>
                              {BRAND_CATEGORY_LABELS[key] ?? key}
                            </MenuItem>
                          ))}
                          {categoryValue &&
                            categoryValue !== "unknown" &&
                            !BRAND_CATEGORIES.includes(categoryValue) && (
                              <MenuItem value={categoryValue}>
                                {categoryValue}
                              </MenuItem>
                            )}
                        </Select>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {row.paidMentions ?? 0}
                        </Typography>
                        {row.lastPaidMentionAt && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(row.lastPaidMentionAt).toLocaleDateString(
                              "es-CL",
                            )}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: 280 }}
                        >
                          {(row.aliases ?? []).slice(0, 4).join(", ") || "—"}
                          {(row.aliases?.length ?? 0) > 4
                            ? ` +${row.aliases.length - 4}`
                            : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={row.isActive ? "Activa" : "Inactiva"}
                            color={row.isActive ? "success" : "default"}
                            variant={row.isActive ? "filled" : "outlined"}
                          />
                          {!row.canSponsor && (
                            <Chip
                              size="small"
                              label="No auspicia"
                              variant="outlined"
                            />
                          )}
                          {!row.s3Key && !row.logoUrl && (
                            <Chip
                              size="small"
                              label="Sin imagen"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        {saveState === "saving" && (
                          <CircularProgress size={18} />
                        )}
                        {saveState === "saved" && (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                        {saveState === "error" && (
                          <ErrorIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="Filas"
        />
      </Paper>

      <BrandAnalysisDrawer
        brandId={selectedBrandId}
        brandSummary={selectedBrandSummary}
        onClose={closeBrandDrawer}
      />

      <Dialog
        open={confirmBulkDelete}
        onClose={() => !bulkDeleteMutation.isPending && setConfirmBulkDelete(false)}
      >
        <DialogTitle>
          Eliminar {selected.size} marca{selected.size === 1 ? "" : "s"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta acción no se puede deshacer. Las menciones asociadas también se
            eliminarán.
          </DialogContentText>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {selectedNames.slice(0, 10).join(", ")}
            {selectedNames.length > 10 ? ` y ${selectedNames.length - 10} más` : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmBulkDelete(false)}
            disabled={bulkDeleteMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => bulkDeleteMutation.mutate(Array.from(selected.keys()))}
            disabled={bulkDeleteMutation.isPending}
          >
            {bulkDeleteMutation.isPending ? "Eliminando…" : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Container>
  );
};

export default BrandAnalysisAdmin;
